import { createEffect, createMemo, mergeProps, onCleanup, type JSX } from "solid-js";
import { Portal } from "solid-js/web";
import {
  createFloating,
  type FallbackPlacements,
  type Placement,
  type ReferenceElement,
} from "~/util/html/floating";
import clsx from "~/util/html/style";
import { hidePopover, showPopover, supportsPopover } from "~/util/html/top_layer";

type FloatingMount = "auto" | HTMLElement;

export type FloatingProps = {
  reference: () => ReferenceElement | undefined;
  open: () => boolean;
  placement?: Placement;
  fallbackPlacements?: FallbackPlacements;
  offset?: number;
  /** Poll every animation frame for position changes (e.g. virtual mouse-following references). */
  animationFrame?: boolean;
  class?: string;
  style?: JSX.CSSProperties;
  mount?: FloatingMount;
  floatingRef?: (element: HTMLDivElement) => void;
  children: JSX.Element;
};

function resolveAutoMount(reference: ReferenceElement | undefined) {
  if (!(reference instanceof Element)) {
    return document.body;
  }

  const modal = reference.closest("dialog[open]");
  return modal instanceof HTMLElement ? modal : document.body;
}

type LayerOrigin = () => ReferenceElement | Node | undefined;

// Where each portalled layer was opened from: a panel's reference, or the spot a modal dialog was
// rendered. A popover can then tell a layer opened from inside it (a picker in a confirm popover, a
// lightbox in a thread) from the rest of the page, since all of them portal out to body.
const layerOrigins = new WeakMap<Element, LayerOrigin>();

/** Records where a portalled layer was opened from; see `containsThroughLayers`. */
export function registerLayerOrigin(layer: Element, origin: LayerOrigin) {
  layerOrigins.set(layer, origin);
}

/** Whether `node` sits in `container`, or in a panel or dialog opened from something inside it,
 *  however deep. */
export function containsThroughLayers(container: Element, node: Node | null): boolean {
  if (!node) return false;
  if (container.contains(node)) return true;

  const element = node instanceof Element ? node : node.parentElement;
  const layer = element?.closest("[data-floating], dialog");
  const origin = layer ? layerOrigins.get(layer)?.() : undefined;
  return origin instanceof Node && containsThroughLayers(container, origin);
}

const OPEN_PANEL_SELECTOR = "[data-floating][data-open]";

/**
 * Whether any popover, tooltip or menu panel is showing; global key handlers stand down while one is.
 * Read from each panel's own open state, not from layout, so every browser answers alike.
 */
export function isFloatingPanelOpen(): boolean {
  return document.querySelector(OPEN_PANEL_SELECTOR) !== null;
}

/**
 * Whether something sits above `panel` and so takes Escape first: a panel opened from inside it, or
 * a modal opened over it. A modal keeps focus inside itself, so the one above is the open dialog
 * holding focus; a dialog stacked below the panel's own never has it.
 */
export function hasLayerAbove(panel: Element): boolean {
  const focusedModal = document.activeElement?.closest("dialog[open]");
  return (
    [...document.querySelectorAll(OPEN_PANEL_SELECTOR)].some(
      (other) => other !== panel && containsThroughLayers(panel, other),
    ) ||
    (focusedModal != null && !focusedModal.contains(panel))
  );
}

export function Floating(_props: FloatingProps) {
  const props = mergeProps({ mount: "auto" as const }, _props);
  let elementRef!: HTMLDivElement;
  let cleanup: (() => void) | null = null;

  const mountElement = createMemo(() => {
    props.open();
    return props.mount === "auto" ? resolveAutoMount(props.reference()) : props.mount;
  });

  // Without the Popover API the panel stays a fixed element in body, shown and hidden with `hidden`.
  const useTopLayerPopover = createMemo(
    () => mountElement() === document.body && supportsPopover(),
  );

  createEffect(() => {
    const reference = props.reference();
    const open = props.open();

    if (!reference || !open) {
      if (cleanup) {
        cleanup();
        cleanup = null;
      }
      return;
    }

    if (cleanup) {
      return;
    }

    cleanup = createFloating({
      reference,
      floating: elementRef,
      config: {
        placement: props.placement,
        fallbackPlacements: props.fallbackPlacements,
        offset: props.offset,
        animationFrame: props.animationFrame,
      },
    });
  });

  createEffect(() => {
    if (!useTopLayerPopover()) {
      return;
    }

    if (props.open()) {
      showPopover(elementRef);
    } else {
      hidePopover(elementRef);
    }
  });

  onCleanup(() => {
    if (cleanup) {
      cleanup();
    }
  });

  return (
    <Portal mount={mountElement()}>
      <div
        ref={(element) => {
          elementRef = element;
          registerLayerOrigin(element, props.reference);
          props.floatingRef?.(element);
        }}
        popover={useTopLayerPopover() ? "manual" : undefined}
        hidden={!useTopLayerPopover() && !props.open()}
        data-floating
        data-open={props.open() ? "" : undefined}
        class={clsx(
          // Override UA [popover] background and overflow in top-layer mode.
          "fixed z-floating-attachment overflow-visible bg-transparent",
          props.class,
        )}
        style={props.style}
      >
        {props.children}
      </div>
    </Portal>
  );
}
